import { CONNECTORS, type ConnectorMeta } from '@research-assistant/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useConnectorPresence } from '@/integration/hooks/useConnections.js';
import { getRuntimeConfig, loadRuntimeConfig } from '@/lib/runtime-config.js';
import { useServerEvent } from '@/integration/runtime/server-events.js';
import { BROWSERSTACK_CONNECTOR } from '@/integration/components/connectors/ConnectorsDirectory.js';
import * as api from '@/integration/services/integration-api.js';
import type { ConnectorPresence } from '@/integration/services/integration-api.js';
import {
  connectorStatusQueryOptions,
  type ConnectorStatusSource,
  type QualityConnectorStatusList,
} from '@/lib/api/quality-setup.js';

/** A connector some Quality surface depends on, by its identity provider id. */
export type QualityConnectorId =
  | 'jira'
  | 'confluence'
  | 'testrail'
  | 'zephyr'
  | 'xray'
  | 'azure_devops'
  | 'github'
  | 'gitlab'
  | 'browserstack'
  | 'reportportal';

/** What a badge shows: brand colour, grey, or a red dot for a connection that needs a reconnect. */
export type ConnectorBadgeState = 'connected' | 'disconnected' | 'error';

/** One connector's state, and the store that answered for it. */
export interface ConnectorBadgeStatus {
  state: ConnectorBadgeState;
  source: ConnectorStatusSource;
  /** The connection identity reports as dead, so a reconnect can land on it rather than beside it. */
  reconnectConnectionId: string | null;
  /** The workspace owner whose connection the caller chose to run Quality through. */
  chosenOwnerId?: string | null;
  /** Owners whose shared connection the caller may choose instead of connecting their own. */
  ownerOptions?: string[];
}

/** The display name of each connector, for the badge and its modal. */
export const QUALITY_CONNECTOR_NAMES: Record<QualityConnectorId, string> = {
  jira: 'Jira',
  confluence: 'Confluence',
  testrail: 'TestRail',
  zephyr: 'Zephyr Scale',
  xray: 'Xray',
  azure_devops: 'Azure DevOps',
  github: 'GitHub',
  gitlab: 'GitLab',
  browserstack: 'BrowserStack',
  reportportal: 'Report Portal',
};

/**
 * Whether a Quality status row speaks for `provider`. Quality reports identity ids
 * (`azure_devops`); an older quality-service said `azure`, which is read as the same connector.
 */
function statusRowMatches(
  rowProvider: string,
  provider: QualityConnectorId,
): boolean {
  return (
    rowProvider === provider ||
    (provider === 'azure_devops' && rowProvider === 'azure')
  );
}

/**
 * Merges the two stores a connector can live in. Quality's status covers the project's own accounts
 * as well as the workspace connector; identity's presence is the fallback while Quality's route is
 * missing (`null`) and the only source of the reconnect flag. Either store saying "connected" is
 * enough, because a run can use either.
 */
export function mergeConnectorStatus(
  provider: QualityConnectorId,
  quality: QualityConnectorStatusList | null | undefined,
  presence: ConnectorPresence | undefined,
): ConnectorBadgeStatus {
  const row = quality?.connectors.find((candidate) =>
    statusRowMatches(candidate.provider, provider),
  );
  const entry = presence?.providers[provider];
  const deadConnection = entry?.connections.find(
    (connection) => connection.needsReconnect === true,
  );
  const fromProject = row?.connected === true && row.source === 'project';
  /** Quality names a chosen owner only while that owner's connection is the one it uses. */
  const fromChosenOwner = row?.connected === true && Boolean(row.chosenOwnerId);
  const identityConnected = entry?.connected === true;
  const connected = row?.connected === true || identityConnected;
  /**
   * Which store answered. Quality's own answer wins; identity is named only when its presence says
   * connected. A connected row with no source this build knows stays `null`: connected, from an
   * unknown store, so nothing claims a workspace connector that presence does not show.
   */
  const source: ConnectorStatusSource =
    row?.connected === true
      ? (row.source ?? (identityConnected ? 'identity' : null))
      : identityConnected
        ? 'identity'
        : null;
  const ownerChoice = {
    chosenOwnerId: row?.chosenOwnerId,
    ownerOptions: row?.ownerOptions,
  };
  if (deadConnection && !fromProject && !fromChosenOwner) {
    return {
      state: 'error',
      source,
      reconnectConnectionId: deadConnection.connectionId,
      ...ownerChoice,
    };
  }
  return {
    state: connected ? 'connected' : 'disconnected',
    source,
    reconnectConnectionId: null,
    ...ownerChoice,
  };
}

/** One connector's badge state in a project, from Quality's status and the workspace presence. */
export function useConnectorBadgeStatus(
  provider: QualityConnectorId,
  projectId: string | undefined,
  workspaceId: string | null,
): ConnectorBadgeStatus {
  const qualityStatus = useQuery(connectorStatusQueryOptions(projectId));
  const presence = useConnectorPresence(workspaceId);
  return mergeConnectorStatus(provider, qualityStatus.data, presence.data);
}

/**
 * The catalog entry the connect flow needs (auth kind, OAuth availability). Shares the directory's
 * catalog query; a provider the catalog does not list yet gets a minimal credentials-only entry.
 */
export function useConnectorMeta(provider: string): ConnectorMeta | undefined {
  const queryClient = useQueryClient();
  const [pendingRefreshes, setPendingRefreshes] = useState(0);
  useServerEvent('connector.changed', () => {
    if (provider !== 'powerbi') return;
    setPendingRefreshes((count) => count + 1);
    void loadRuntimeConfig(true)
      .then(() => queryClient.invalidateQueries({ queryKey: ['connections'] }))
      .finally(() => setPendingRefreshes((count) => count - 1));
  });
  const catalog = useQuery({
    queryKey: ['connectors-catalog'],
    queryFn: api.getConnectorsCatalog,
    staleTime: 10_000,
    placeholderData: [...CONNECTORS],
  });
  if (
    provider === 'powerbi' &&
    (pendingRefreshes > 0 || getRuntimeConfig().connectors.powerbi !== true)
  )
    return undefined;
  const listed = (catalog.data ?? []).find((meta) => meta.id === provider);
  if (listed) return listed;
  if (provider === 'powerbi') {
    const builtin = CONNECTORS.find((meta) => meta.id === provider);
    if (builtin) return builtin;
  }
  if (provider === 'browserstack') return BROWSERSTACK_CONNECTOR;
  const name =
    QUALITY_CONNECTOR_NAMES[provider as QualityConnectorId] ?? provider;
  return {
    id: provider as ConnectorMeta['id'],
    name,
    vendor: name,
    authKind: 'api-key',
    description: '',
    source: 'builtin',
  };
}

/** The connector a Quality source name depends on (`azure` is Azure DevOps), or null for none. */
export function connectorForSource(
  source: string | null | undefined,
): QualityConnectorId | null {
  if (source === 'azure') return 'azure_devops';
  if (source === 'testrail' || source === 'zephyr' || source === 'xray')
    return source;
  return null;
}
